import { Box, Button, CircularProgress, MenuItem, Pagination, Select, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid/DataGrid/DataGrid";
import { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { GridValueGetterParams } from "@mui/x-data-grid/models/params/gridCellParams";
import RefreshIcon from '@mui/icons-material/Refresh';
import React from "react";
import { getUserWebGameSessionsPaginated, getWebGames } from "../Api";
import { WebGameListItemDto, WebGameSessionHistoryDto } from "../models/dtos/WebGameDto";

interface UserGamesTableProps {
    userId: number;
    userLang?: string;
}

export default function UserGamesTable(props: UserGamesTableProps) {
    const [state, setState] = React.useState({
        games: [] as WebGameListItemDto[],
        items: [] as WebGameSessionHistoryDto[],
        loaded: false,
        page: 1,
        pageSize: 25,
        totalPages: 1
    });

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Session ID', width: 220 },
        {
            field: 'gameCode',
            headerName: 'Game',
            width: 180,
            valueGetter: (params: GridValueGetterParams) =>
                state.games.find(game => game.code === params.row.gameCode)?.name ?? params.row.gameCode
        },
        { field: 'status', headerName: 'Status', width: 120 },
        { field: 'betAmount', headerName: 'Bet', width: 100 },
        { field: 'returnAmount', headerName: 'Return', width: 100 },
        {
            field: 'createdAt',
            headerName: 'CreatedAt',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => new Date(params.row.createdAt).toLocaleString()
        },
        {
            field: 'closedAt',
            headerName: 'ClosedAt',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => params.row.closedAt ? new Date(params.row.closedAt).toLocaleString() : '-'
        },
        {
            field: 'metadata',
            headerName: 'Metadata',
            width: 260,
            sortable: false,
            valueGetter: (params: GridValueGetterParams) => JSON.stringify(params.row.metadata ?? {})
        },
        {
            field: 'stats',
            headerName: 'Stats',
            width: 220,
            sortable: false,
            valueGetter: (params: GridValueGetterParams) => JSON.stringify(params.row.stats ?? {})
        },
    ];

    const fetchData = React.useCallback(async (page = 1) => {
        setState(prevState => ({ ...prevState, loaded: false }));
        const offset = page - 1;
        const [games, response] = await Promise.all([
            getWebGames(props.userLang),
            getUserWebGameSessionsPaginated(props.userId, offset, state.pageSize)
        ]);
        const totalPages = Math.max(1, Math.ceil((response?.totalItems ?? 0) / state.pageSize));
        setState(prevState => ({
            ...prevState,
            games,
            items: response?.items ?? [],
            loaded: true,
            page,
            totalPages
        }));
    }, [props.userId, props.userLang, state.pageSize]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        fetchData(page);
    };

    const handlePageSelectChange = (event: SelectChangeEvent<number>) => {
        fetchData(Number(event.target.value));
    };

    React.useEffect(() => {
        fetchData(1);
    }, [fetchData]);

    return (
        <Stack spacing={2}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={() => fetchData(state.page)}
                >
                    Refresh
                </Button>
            </Box>

            {!state.loaded ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={64} />
                </Box>
            ) : state.items.length === 0 ? (
                <Typography color="text.secondary">No web game sessions were found for this user.</Typography>
            ) : (
                <>
                    <DataGrid
                        sx={{ width: '100%' }}
                        rows={state.items}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: { page: 0, pageSize: state.pageSize },
                            },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        hideFooterPagination
                        hideFooter
                    />
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ mt: 1, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                        <Pagination
                            count={state.totalPages}
                            page={state.page}
                            onChange={handlePageChange}
                            color="primary"
                            showFirstButton
                            showLastButton
                        />
                        <Select
                            value={state.page}
                            onChange={handlePageSelectChange}
                            label="Navigate to ..."
                            style={{ width: '150px' }}
                        >
                            {Array.from({ length: state.totalPages }, (_, i) => i + 1).map(page => (
                                <MenuItem key={page} value={page}>
                                    Page {page}
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>
                </>
            )}
        </Stack>
    );
}
